
import { ref, computed, watch, nextTick, defineComponent, onMounted } from 'vue'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import Divider from 'primevue/divider'

// Deposit Components
import DepositAuto from '../components/deposit/Auto.vue'
import DepositAutoSlip from '../components/deposit/AutoSlip.vue'
import DepositDecimal from '../components/deposit/Decimal.vue'
import DepositP2P from '../components/deposit/P2P.vue'
import DepositThPay from '../components/deposit/ThPay.vue'
import DepositTrueWallet from '../components/deposit/TrueWallet.vue'
import DepositAutoPeer from '../components/deposit/AutoPeer.vue'
import { useAuthStore } from '../stores/auth';
import { useWebStore } from '../stores/web';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import DepositAutoSlipNew from '../components/deposit/AutoSlipNew.vue';

export default {
	name: 'WalletDeposit',
	components: {
		Tabs,
		TabPanels,
		TabPanel,
		TabList,
		Tab,
		Divider,

		DepositAuto,
		DepositAutoSlip,
		DepositDecimal,
		DepositP2P,
		DepositThPay,
		DepositTrueWallet,
		DepositAutoPeer,
	},
	props: {
		// เว้นว่าง = ไม่เจาะจงแท็บ ให้ลำดับช่องทางจาก manager เป็นตัวตัดสิน
		// (ลำดับ 1 ที่หลังบ้านตั้งไว้). caller ที่ "ต้อง" เปิดแท็บใดแท็บหนึ่งค่อยส่งค่ามา
		tabs: {
			type: String,
			default: '',
		},
		// ตั้ง true เมื่อ caller "ต้อง" เปิดที่แท็บ `tabs` จริง ๆ และห้าม default
		// autopeer-first มาทับ (ใช้โดย fallback ของ AutoPeer ตอนเจอ
		// SWITCH_TO_SLIP_CODES เช่น 60001). เป็น flag แยกเพราะเทียบค่า tabs กับ
		// default ไม่ได้ — แต่ละไซต์ตั้ง default ไม่เหมือนกัน ('auto' / 'slip')
		lockTab: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const auth = useAuthStore();
		auth.hydrate()

		const web = useWebStore();
		web.hydrate();
		const { webPermission, managerConfig } = storeToRefs(web);
		const { profile } = storeToRefs(auth);

		onMounted(() => {
			const updateP2p = sessionStorage.getItem('update_p2p')
			if (updateP2p === '1') {
				autoDefault.value = false
				tabs.value = 'p2p'
				sessionStorage.removeItem('update_p2p')
			}
		})

		const tabs = ref<string>(props.tabs)

		// true = แท็บปัจจุบันมาจาก default อัตโนมัติ (ยังปรับตามลำดับของ manager ได้)
		// false = ถูกเจาะจงแล้ว — โดย caller, โดย autopeer-first หรือโดยลูกค้ากดเอง
		const autoDefault = ref<boolean>(!props.tabs)
		const selectTab = (key: string) => {
			autoDefault.value = false
			tabs.value = key
		}

		// Registry mapping a manager deposit-method `code` to its channel
		// component + default title. The operator picks WHICH channels show and
		// in WHAT order via the manager (managerConfig.deposit); this registry
		// just resolves each code to a component. The label comes from the
		// operator's method name. Keys cover the manager's real codes plus a few
		// aliases so a slightly different code on another site still resolves.
		const CHANNEL_COMPONENTS: Record<string, any> = {
			fast: DepositAuto,
			auto: DepositAuto,
			'auto-slip': DepositAutoSlipNew,
			slip: DepositAutoSlipNew,
			decimal: DepositDecimal,
			p2p: DepositP2P,
			autopeer: DepositAutoPeer,
			qr: DepositThPay,
			thpay: DepositThPay,
			truewallet: DepositTrueWallet,
		}

		const CHANNEL_TITLES: Record<string, string> = {
			fast: 'ฝากด่วน',
			auto: 'ฝากด่วน',
			'auto-slip': 'ฝากสลิปออโต้',
			slip: 'ฝากสลิปออโต้',
			decimal: 'ฝากทศนิยม',
			p2p: 'ฝาก P2P',
			autopeer: 'Auto Peer',
			qr: 'Qr Code',
			thpay: 'Qr Code',
			truewallet: 'TrueWallet',
		}

		// Resolve a manager method to a canonical channel key: prefer the
		// explicit code, else fall back to the name for methods whose code was
		// left unset (autopeer is stored with a null code today).
		const resolveChannelKey = (m: { code?: string; name?: string }): string => {
			const code = (m.code || '').trim()
			if (code && CHANNEL_COMPONENTS[code]) return code
			const name = (m.name || '').toLowerCase()
			if (name.includes('autopeer') || name.includes('auto peer')) return 'autopeer'
			return ''
		}

		// Default channel set (order + labels) used when the manager config is
		// unavailable — preserves the theme's prior hardcoded behavior so a
		// manager outage never empties the deposit popup.
		const FALLBACK_KEYS = ['auto', 'slip', 'decimal', 'autopeer', 'thpay', 'truewallet']

		// Ordered list of active channels: manager-driven when config is present
		// (enabled + sorted upstream, resolved to components we can render),
		// else the fallback set.
		const baseChannels = computed<Array<{ key: string; title: string; component: any }>>(() => {
			const list = (managerConfig.value?.deposit ?? [])
				.map((m) => ({ m, key: resolveChannelKey(m) }))
				.filter((x) => x.key)
				.map(({ m, key }) => ({
					key,
					title: m.name || CHANNEL_TITLES[key] || key,
					component: CHANNEL_COMPONENTS[key],
				}))
			if (list.length) return list
			return FALLBACK_KEYS.map((key) => ({
				key,
				title: CHANNEL_TITLES[key],
				component: CHANNEL_COMPONENTS[key],
			}))
		})

		// Customers who have used Auto Peer (profile.isDeposit.isAutoPeer)
		// are pushed toward it — banks are unreliable so we steer returning autopeer
		// users back to it. Only when it's genuinely available: the site permission
		// must be on AND autopeer must be in the active list; otherwise the normal
		// manager order stands untouched.
		const forceAutoPeerFirst = computed<boolean>(() =>
			(profile.value?.isDeposit?.isAutoPeer ?? false) &&
			(webPermission.value?.isDeposit?.isAutoPeer ?? false),
		);

		// Active channels with Auto Peer hoisted to the front for pushed customers.
		const channels = computed<Array<{ key: string; title: string; component: any }>>(() => {
			const list = baseChannels.value
			if (!forceAutoPeerFirst.value) return list
			const idx = list.findIndex((c) => c.key === 'autopeer')
			if (idx <= 0) return list // autopeer absent or already first
			const reordered = list.slice()
			const [ap] = reordered.splice(idx, 1)
			reordered.unshift(ap)
			return reordered
		})

		const tabComponentMap = computed<Record<string, any>>(() =>
			Object.fromEntries(channels.value.map((c) => [c.key, c.component])),
		)
		const tabTitleMap = computed<Record<string, string>>(() =>
			Object.fromEntries(channels.value.map((c) => [c.key, c.title])),
		)

		// Several registry keys resolve to the SAME channel component — 'slip' and
		// 'auto-slip' both render DepositAutoSlipNew, 'fast'/'auto' both render
		// DepositAuto, 'qr'/'thpay' both render DepositThPay. Which key ends up in
		// `channels` is whatever the operator configured in the manager, so a
		// caller asking for 'slip' must still land on the auto-slip channel when
		// the operator called it 'auto-slip' — otherwise the fallback below drops
		// it on list[0] (often AutoPeer, the channel it was escaping).
		const equivalentKey = (want: string, list: Array<{ key: string }>): string | undefined => {
			const wanted = CHANNEL_COMPONENTS[want]
			if (!wanted) return undefined
			return list.find((c) => CHANNEL_COMPONENTS[c.key] === wanted)?.key
		}

		// เลือกแท็บเริ่มต้น + คุมให้แท็บที่เลือกอยู่ยังใช้งานได้เสมอ
		//  - ยังไม่เจาะจง (autoDefault) → ใช้ช่องทางลำดับแรกที่ operator ตั้งไว้ใน manager
		//    ซึ่งกรณีลูกค้าที่เคยฝาก Auto Peer คือ autopeer เอง เพราะ channels hoist ไว้ให้แล้ว
		//  - เจาะจงแล้ว แต่แท็บนั้นถูกปิด → หา key ที่ render component เดียวกัน ไม่งั้นตกไปตัวแรก
		watch(channels, (list) => {
			if (!list.length) return
			if (autoDefault.value) {
				tabs.value = list[0].key
				return
			}
			if (!list.some((c) => c.key === tabs.value)) {
				tabs.value = equivalentKey(tabs.value, list) ?? list[0].key
			}
		}, { immediate: true })

		// Open the popup on the Auto Peer tab for pushed customers (once). Fires
		// again if the profile resolves after mount. Skipped during the p2p-update
		// flow so that deliberate redirect wins, and marked done so a later manual
		// tab change sticks.
		const autoPeerDefaulted = ref(false)
		watch([channels, forceAutoPeerFirst], ([list, force]) => {
			if (autoPeerDefaulted.value || !force) return
			if (!list.some((c) => c.key === 'autopeer')) return
			autoPeerDefaulted.value = true
			if (props.lockTab) return
			if (sessionStorage.getItem('update_p2p') === '1') return
			autoDefault.value = false
			tabs.value = 'autopeer'
		}, { immediate: true })

		// ถ้าช่องทาง AutoPeer ปิดให้บริการ → พาไปแท็บฝากสลิปออโต้แทนทันที
		// ต้องสลับหลัง nextTick ไม่งั้น prop ของ Tabs ไม่เปลี่ยนข้าม tick แล้ว state ภายในค้างที่แท็บเดิม
		// (guard บน slip ที่มีอยู่จริง เพื่อไม่ให้เด้งไปแท็บที่ operator ปิด)
		const isAutoPeerActive = computed<boolean>(() => webPermission.value?.isDeposit?.isAutoPeer ?? false);
		watch([tabs, isAutoPeerActive], async ([tab, active]) => {
			if (tab !== 'autopeer' || active) return;
			const slipKey = equivalentKey('slip', channels.value);
			if (!slipKey) return;
			await nextTick();
			// pin ไว้ ไม่งั้น default ตามลำดับ manager จะดันกลับมาที่ autopeer ที่เพิ่งหนีออกมา
			autoDefault.value = false;
			tabs.value = slipKey;
		}, { immediate: true });

		return {
			tabs,
			selectTab,
			tabComponentMap,
			tabTitleMap,
		}
	},
};
